import { useQuery } from '@tanstack/react-query'
import { BookOpenText, ClipboardList, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { useAuth } from '@/features/auth/useAuth'
import { familyCourseQuery } from '@/features/lms/api'
import { FileChips } from '@/features/lms/components/FileChips'
import { MaterialCard } from '@/features/lms/components/MaterialCard'
import { RichText } from '@/features/lms/components/RichText'
import { SubmitDialog } from '@/features/lms/components/SubmitDialog'
import { formatPoints, workStatus, workStatusMeta } from '@/features/lms/status'
import type { FamilyCourse } from '@/features/lms/types'
import { useChild } from '@/features/lms/useChild'
import { familyPortalLabel, useGuardians } from '@/features/auth/guardians'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { tAny } from '@/i18n'
import { formatDateTime } from '@/utils/format'

type Item = FamilyCourse['assignments'][number]

/** A course in the family portal: the published contents and the homework, with the student's work. */
export function FamilyLmsCoursePage() {
  const { t } = useTranslation('lms')
  const guardians = useGuardians()
  const { user } = useAuth()
  const subjectId = Number(useParams().subjectId)
  const { child } = useChild()
  const studentId = child?.student.id ?? 0
  const course = useQuery({ ...familyCourseQuery(studentId, subjectId), enabled: studentId > 0 })
  const [handing, setHanding] = useState<Item | null>(null)
  const isStudent = user?.role === 'student'
  useDocumentTitle(course.data?.course.subject.name ?? 'Online learning')

  if (!child || course.isPending) return <LoadingState />
  if (course.error)
    return <ErrorState description={errorMessage(course.error)} onRetry={() => void course.refetch()} />
  const c = course.data

  return (
    <>
      <PageHeader
        title={c.course.subject.name}
        description={c.course.section.name}
        breadcrumbs={[
          { label: tAny(familyPortalLabel(guardians)), to: '/portal' },
          { label: t('Online learning'), to: `/portal/learning?student=${studentId}` },
          { label: c.course.subject.name },
        ]}
        homeHref="/portal"
      />
      <div className="grid items-start gap-6 lg:grid-cols-5">
        <section className="space-y-3 lg:col-span-3" aria-labelledby="contents-title">
          <h2 id="contents-title" className="flex items-center gap-2 text-sm font-semibold text-ink">
            <BookOpenText className="size-4 text-brand-600" aria-hidden="true" />
            {t('Contents')}
          </h2>
          {c.materials.length === 0 ? (
            <Card>
              <EmptyState compact icon={<BookOpenText />} title={t('Nothing published yet')} />
            </Card>
          ) : (
            c.materials.map((m) => <MaterialCard key={m.id} material={m} />)
          )}
        </section>
        <section className="space-y-3 lg:col-span-2" aria-labelledby="homework-title">
          <h2 id="homework-title" className="flex items-center gap-2 text-sm font-semibold text-ink">
            <ClipboardList className="size-4 text-brand-600" aria-hidden="true" />
            {t('Assignments')}
          </h2>
          {c.assignments.length === 0 ? (
            <Card>
              <EmptyState compact icon={<ClipboardList />} title={t('No homework yet')} />
            </Card>
          ) : (
            c.assignments.map((a) => {
              const status = workStatusMeta[workStatus(a, a.submission)]
              const canHandIn = isStudent && a.open && !a.submission?.gradedAt
              return (
                <Card key={a.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-ink">{a.title}</h3>
                      <p className="text-xs text-muted">
                        {t('Due {{date}}', { date: formatDateTime(a.dueAt) })}
                        {a.maxPoints ? ` · ${t('out of {{max}}', { max: a.maxPoints })}` : ''}
                      </p>
                    </div>
                    <Badge tone={status.tone} size="sm">
                      {t(status.label)}
                    </Badge>
                  </div>
                  {a.instructions ? <RichText text={a.instructions} className="mt-3" /> : null}
                  {a.files.length ? (
                    <div className="mt-3">
                      <FileChips files={a.files} />
                    </div>
                  ) : null}
                  {a.submission?.gradedAt ? (
                    <div className="mt-3 rounded-control bg-success-50 p-3 text-sm">
                      <p className="font-semibold text-success-700">
                        {a.maxPoints ? formatPoints(a.submission.points, a.maxPoints) : t('Graded')}
                        {a.submission.gradedBy ? (
                          <span className="font-normal"> · {a.submission.gradedBy}</span>
                        ) : null}
                      </p>
                      {a.submission.feedback ? (
                        <p className="mt-1 text-ink-soft">{a.submission.feedback}</p>
                      ) : null}
                    </div>
                  ) : a.submission ? (
                    <p className="mt-3 text-xs text-muted">
                      {t('Handed in {{date}}', { date: formatDateTime(a.submission.submittedAt) })}
                    </p>
                  ) : !a.open ? (
                    <p className="mt-3 text-xs text-danger-700">{t('Closed: the due date has passed.')}</p>
                  ) : null}
                  {canHandIn ? (
                    <Button
                      className="mt-3"
                      size="sm"
                      variant={a.submission ? 'secondary' : 'primary'}
                      leftIcon={<Send />}
                      onClick={() => setHanding(a)}
                    >
                      {a.submission ? t('Change my work') : t('Hand in my work')}
                    </Button>
                  ) : null}
                </Card>
              )
            })
          )}
        </section>
      </div>
      {handing ? (
        <SubmitDialog
          studentId={studentId}
          assignment={handing}
          submission={handing.submission}
          onOpenChange={(o) => !o && setHanding(null)}
        />
      ) : null}
    </>
  )
}
